"use client";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { zustandStore } from "@/app/zustand-store";
import cn from "@/utils/cn";
import TextComponent from "@/app/_components/TextComponent";
import {
  ReportIcon,
  ReorderIcon,
  DropDownEllipse,
  DropdownCreateNewIcon,
  DropdownRadioUnselectedIcon,
  DropdownRadioSelectedIcon,
  DropdownSaveIcon,
  DropdownArrowDownIcon,
  DropdownArrowUpIcon,
} from "@/assets/svg-icons/svg-icons";
import axios from "axios";
import { useRouter, usePathname } from "next/navigation";

interface FormSelectorDropdownProps {
  invertColors?: boolean;
  truncateLength?: number;
  disabled?: boolean;
  isEditPage?: boolean;
}

const FormSelectorDropdown: React.FC<FormSelectorDropdownProps> = ({
  invertColors = false,
  truncateLength = 15,
  disabled = false,
  isEditPage = false,
}) => {
  const {
    data: { forms, selectedFormId },
    actions,
  } = zustandStore();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newFormName, setNewFormName] = useState("Create New Form");
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [editingFormName, setEditingFormName] = useState("");
  const [showOptionsForId, setShowOptionsForId] = useState<string | null>(null);
  const [hoveredFormId, setHoveredFormId] = useState<string | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  const selectedForm =
    forms?.find((f) => f.formId === selectedFormId)?.name || null;

  const fetchForms = useCallback(async () => {
    try {
      const response = await axios.get("/api/forms");
      actions.setForms(response.data.forms);
      actions.setSelectedFormId(response.data.selectedFormId);
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  }, [actions]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        resetState();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if ((isCreatingNew || editingFormId) && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreatingNew, editingFormId]);

  const resetState = useCallback(() => {
    setIsOpen(false);
    setIsCreatingNew(false);
    setNewFormName("Create New Form");
    setShowOptionsForId(null);
    setEditingFormId(null);
    setEditingFormName("");
    setHoveredFormId(null);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setReorderMode(false);
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleFormSelect = useCallback(
    async (formId: string) => {
      // Don't select if in reorder mode
      if (reorderMode) return;

      actions.setSelectedFormId(formId);
      resetState();
      try {
        await axios.put("/api/users/selected-form", { formId });
        if (pathname.startsWith("/dashboard/form-builder/")) {
          router.push(`/dashboard/form-builder/${formId}`);
        }
      } catch (error) {
        console.error("Error updating selected form:", error);
      }
    },
    [actions, resetState, pathname, router, reorderMode],
  );

  useEffect(() => {
    // Auto-select the first form when there's no valid selection. Covers both
    // the initial (empty) state and a stale selection left over from another
    // workspace after switching — the stored id won't be in the scoped `forms`,
    // so we fall through to the first form instead of showing "Select a Form".
    if (forms && forms.length > 0) {
      const selectionIsValid = forms.some((f) => f.formId === selectedFormId);
      if (!selectionIsValid) {
        handleFormSelect(forms[0].formId);
      }
    }
  }, [forms, selectedFormId, handleFormSelect]);

  const handleCreateNewClick = () => {
    setIsCreatingNew(true);
    setNewFormName("");
  };

  const handleNewFormNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewFormName(e.target.value);
  };

  const handleCreateForm = async () => {
    if (newFormName.trim() && newFormName !== "Create New Form") {
      try {
        const response = await axios.post("/api/forms", {
          name: newFormName.trim(),
        });
        const newForm = response.data;
        await fetchForms();
        handleFormSelect(newForm.formId);
        router.push(`/dashboard/form-builder/${newForm.formId}`);
      } catch (error) {
        console.error("Error creating new form:", error);
      } finally {
        setIsCreatingNew(false);
        setNewFormName("Create New Form");
      }
    }
  };

  const handleEditForm = (formId: string, currentName: string) => {
    setEditingFormId(formId);
    setEditingFormName(currentName);
    setShowOptionsForId(null);
  };

  const handleEditFormNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingFormName(e.target.value);
  };

  const handleEditFormSubmit = async () => {
    if (editingFormId && editingFormName.trim()) {
      try {
        await axios.put(`/api/forms/${editingFormId}`, {
          name: editingFormName.trim(),
        });
        await fetchForms();
      } catch (error) {
        console.error("Error updating form:", error);
      } finally {
        setEditingFormId(null);
        setEditingFormName("");
      }
    }
  };

  const handleDeleteForm = async (formId: string) => {
    try {
      await axios.delete(`/api/forms/${formId}`);
      await fetchForms();
    } catch (error) {
      console.error("Error deleting form:", error);
      // Handle the case where deletion is prevented
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        // You could show a toast notification here if needed
        console.log("Cannot delete the last form");
      }
    } finally {
      setShowOptionsForId(null);
    }
  };

  const handleDuplicateForm = async (formId: string) => {
    try {
      // Make API call to duplicate the form
      const response = await axios.post("/api/forms", {
        formId: formId,
      });

      // Update the forms list with the server response
      if (response.data.forms) {
        actions.setForms(response.data.forms);
      } else {
        // Fallback: refetch forms if response structure is different
        await fetchForms();
      }

      // Select the new duplicated form
      if (response.data.selectedFormId) {
        actions.setSelectedFormId(response.data.selectedFormId);
      }
    } catch (error) {
      console.error("Error duplicating form:", error);
    } finally {
      setShowOptionsForId(null);
    }
  };

  const handleEnableReorderMode = () => {
    setReorderMode(true);
    setShowOptionsForId(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent, isEditing: boolean) => {
    if (e.key === "Enter") {
      isEditing ? handleEditFormSubmit() : handleCreateForm();
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (maxLength === 0) return text;
    return text?.length > maxLength
      ? text?.substring(0, maxLength) + "..."
      : text;
  };

  // Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "0.5";
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = "1";
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    if (!reorderMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedIndex === null || draggedIndex === index) {
      return;
    }

    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    if (!reorderMode) return;
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Create new array with reordered items
    const newForms = [...forms];
    const [draggedItem] = newForms.splice(draggedIndex, 1);
    newForms.splice(dropIndex, 0, draggedItem);

    // Update Zustand store
    actions.reorderForms(newForms);

    // Update backend
    try {
      await axios.put(
        "/api/forms/reorder",
        {
          forms: newForms,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      console.log("Form order updated successfully");
    } catch (error) {
      console.error("Error updating form order:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error:", error.response?.data);
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="relative w-fit" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className={cn(
          "flex items-center xl:space-x-2 space-x-1 justify-between xl:px-4 px-2 py-2 h-[60px] focus:outline-none border-white border",
          "xl:text-[10.6px] xl:h-[60px] 2xl:text-base 2xl:rounded-2xl xl:rounded-[10.6px]",
          "h-[36px] text-[8px] flex rounded-lg",
          "2xl:w-full w-full xl:w-full md:w-full",
          disabled ? "cursor-not-allowed" : "cursor-pointer",
          disabled
            ? "bg-kd-secondary-7 text-[#8E8E93] border-none"
            : invertColors
              ? "bg-kd-secondary-7"
              : "border hover:border-kd-lte-blue text-kd-lte-blue",
          !disabled && invertColors
            ? isOpen
              ? "bg-kd-secondary-7 text-kd-secondary-1"
              : "bg-kd-lte-blue text-white"
            : !disabled && isOpen
              ? "bg-kd-secondary-7 text-kd-lte-blue"
              : !disabled && "bg-kd-secondary-7 text-kd-lte-blue",
        )}
      >
        <div className="size-5 2xl:size-7">
          <ReportIcon
            className={cn(
              "mr-2 size-full",
              disabled
                ? "text-kd-secondary-4"
                : !disabled && invertColors && !isOpen
                  ? "text-white"
                  : "text-kd-lte-blue",
            )}
          />
        </div>

        <TextComponent
          type="h4"
          className={cn(
            "grow text-nowrap text-left",
            "md:flex hidden",
            disabled
              ? "text-kd-secondary-4"
              : invertColors
                ? ""
                : "text-kd-secondary-1",
          )}
        >
          {truncateText(selectedForm || "Select a Form", truncateLength)}
        </TextComponent>
        {disabled ? (
          <DropdownArrowDownIcon className="text-kd-secondary-4" />
        ) : isOpen ? (
          <DropdownArrowUpIcon
            className={cn(
              !invertColors
                ? isOpen
                  ? "text-kd-lte-blue"
                  : "text-kd-lte-blue"
                : isOpen
                  ? "text-kd-lte-blue"
                  : "text-white",
            )}
          />
        ) : (
          <DropdownArrowDownIcon
            className={cn(!invertColors ? "text-kd-lte-blue" : "text-white")}
          />
        )}
      </button>

      {isOpen && !disabled && (
        <div className="absolute right-0 z-10 mt-2 w-[280px]">
          <div className="overflow-hidden rounded-2xl border border-kd-lte-blue bg-white shadow-lg">
            <div className="max-h-80 overflow-y-auto custom-scrollbar">
              {isEditPage && (
                <div className="sticky top-0 z-30 flex items-center border-b border-dashed border-gray-300 bg-white p-4 hover:bg-kd-secondary-6">
                  <div className="mr-[7.5px] xl:mr-3">
                    <DropdownCreateNewIcon className=" size-3 text-kd-lte-blue xl:size-4" />
                  </div>
                  {isCreatingNew ? (
                    <>
                      <input
                        ref={inputRef}
                        type="text"
                        value={newFormName}
                        onChange={handleNewFormNameChange}
                        onKeyDown={(e) => handleKeyPress(e, false)}
                        className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                        placeholder="Enter form name"
                      />
                      <div
                        className="ml-2 cursor-pointer"
                        onClick={handleCreateForm}
                      >
                        <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                      </div>
                    </>
                  ) : (
                    <TextComponent
                      type="caption"
                      onClick={handleCreateNewClick}
                      className="cursor-pointer text-[#8E8E93]"
                    >
                      Create New Form
                    </TextComponent>
                  )}
                </div>
              )}

              {forms?.map((form, index) => (
                <div
                  key={form.formId}
                  draggable={reorderMode && editingFormId !== form.formId}
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  className={cn(
                    "group relative flex items-center justify-between p-4 transition-colors hover:bg-kd-secondary-6 hover:text-kd-lte-blue text-[#96A5B8]",
                    reorderMode
                      ? "cursor-grab active:cursor-grabbing"
                      : "cursor-pointer",
                    dragOverIndex === index && draggedIndex !== index
                      ? "border-t-2 border-kd-lte-blue"
                      : "",
                    draggedIndex === index ? "opacity-50" : "opacity-100",
                  )}
                  onMouseEnter={() => setHoveredFormId(form.formId)}
                  onMouseLeave={() => setHoveredFormId(null)}
                  onClick={() =>
                    editingFormId !== form.formId &&
                    handleFormSelect(form.formId)
                  }
                >
                  {/* Show drag handle in reorder mode, otherwise show radio button */}
                  <div className="mr-2">
                    {reorderMode ? (
                      <div
                        className="cursor-grab hover:bg-gray-200 active:cursor-grabbing"
                        onMouseDown={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <ReorderIcon className="text-kd-lte-blue" />
                      </div>
                    ) : (
                      <div className="mr-[1.5px] xl:mr-1">
                        {form.formId === selectedFormId ? (
                          <DropdownRadioSelectedIcon className=" size-3 text-kd-lte-blue xl:size-4" />
                        ) : (
                          <DropdownRadioUnselectedIcon className="size-3 text-[#96A5B8] group-hover:text-[#336DFF] xl:size-4" />
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex grow items-center">
                    {editingFormId === form.formId ? (
                      <input
                        ref={inputRef}
                        type="text"
                        value={editingFormName}
                        onChange={handleEditFormNameChange}
                        onKeyDown={(e) => handleKeyPress(e, true)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full bg-transparent focus:outline-none text-[10.5px] xl:text-[14px] font-normal text-kd-secondary-1 placeholder:text-kd-secondary-3"
                      />
                    ) : (
                      <TextComponent
                        type="caption"
                        className={cn(
                          form.formId === selectedFormId && !reorderMode
                            ? "text-[#05004E] font-medium"
                            : "",
                        )}
                      >
                        {form.name}
                      </TextComponent>
                    )}
                  </div>

                  {/* Hide options menu in reorder mode */}
                  {!reorderMode && (
                    <div
                      className="flex items-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {/* Show checkmark when editing */}
                      {editingFormId === form.formId && (
                        <div
                          className="cursor-pointer"
                          onClick={handleEditFormSubmit}
                        >
                          <DropdownSaveIcon className="size-3 text-kd-lte-blue xl:size-4" />
                        </div>
                      )}

                      {/* Hide ellipsis when editing */}
                      {editingFormId !== form.formId && (
                        <button
                          onClick={() =>
                            setShowOptionsForId(
                              showOptionsForId === form.formId
                                ? null
                                : form.formId,
                            )
                          }
                          className="px-2 text-[#96A5B8] hover:text-gray-600"
                        >
                          <DropDownEllipse className="h-[18px] w-1" />
                        </button>
                      )}

                      {showOptionsForId === form.formId && (
                        <div className="fixed z-20">
                          <div className="absolute -top-4 right-2 w-[123px] origin-top-right overflow-hidden rounded-lg border border-kd-lte-blue bg-[#F8F9FA] shadow-lg">
                            <button
                              onClick={() =>
                                handleEditForm(form.formId, form.name)
                              }
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handleEditForm(form.formId, form.name)
                              }
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Rename
                            </button>
                            <button
                              onClick={() => handleDuplicateForm(form.formId)}
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Duplicate
                            </button>
                            <button
                              onClick={handleEnableReorderMode}
                              className="block w-full px-4 py-2 text-left text-xs text-gray-700 hover:bg-white hover:text-kd-lte-blue"
                            >
                              Reorder
                            </button>
                            {form.formId !== selectedFormId && (
                              <button
                                onClick={() => handleDeleteForm(form.formId)}
                                disabled={forms.length <= 1}
                                className={cn(
                                  "block w-full border-t border-dashed border-gray-200 px-4 py-2 text-left text-xs",
                                  forms.length <= 1
                                    ? "cursor-not-allowed text-gray-400 bg-gray-50"
                                    : "text-[#FA5A7D] hover:bg-white cursor-pointer",
                                )}
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormSelectorDropdown;
